import { css } from 'styled-components';

// Holds the type, the palette and the css helpers only — deliberately NOT the
// camo artwork constant, which lives in `~/common/constants/gateBackdrop`.
//
// Do not merge the two files despite the shared name. `EventPasswordForm`
// imports this module and renders `/secret-event`, which shows NO backdrop;
// moving the `.webp` imports here would pull 345KB of artwork into that route's
// bundle graph for an image it never displays.

/** Artwork to render behind the form, supplied as data by the caller. */
export interface GateBackdrop {
  desktop: string;
  mobile: string;
  /** Media condition under which `mobile` replaces `desktop`. */
  mobileMedia: string;
  /**
   * Overrides `BACKDROP_SCRIM`. Only for artwork measured to clear AA at a lighter
   * value: the override gives up the "any artwork is safe" guarantee, so the
   * artwork must be re-measured whenever the image changes.
   */
  scrim?: string;
}

/**
 * Darkens the artwork so the form stays legible on it. 0.6 is not a taste
 * value: it is the point at which white text clears WCAG AA (4.5:1) even if the
 * lightest pixel in the artwork were pure white, so swapping the image cannot
 * silently break the contrast.
 */
export const BACKDROP_SCRIM = 'rgba(0, 0, 0, 0.6)';

/**
 * The scrim is a gradient layer rather than `filter: brightness()` because
 * `filter` applies to descendants too — it would darken the form's own text
 * along with the artwork. The URL goes through a custom property so the scrim
 * is written once and cannot drift out of sync with the mobile override.
 *
 * Takes the artwork as a parameter rather than hardcoding the camo constant: this
 * module is also imported by `EventPasswordForm` for `gateScreenLayout`, and a `.webp`
 * import here would pull 345KB of artwork into `/secret-event`'s graph for an image it
 * never renders.
 */
export const backdropLayer = ({
  desktop,
  mobile,
  mobileMedia,
  scrim = BACKDROP_SCRIM,
}: GateBackdrop) => css`
  --gate-backdrop: url('${desktop}');

  @media ${mobileMedia} {
    --gate-backdrop: url('${mobile}');
  }

  background-image: linear-gradient(${scrim}, ${scrim}), var(--gate-backdrop);
  background-size: cover;
  background-position: center;
`;

/**
 * The full-viewport centred layout every gate screen sits in. Shared because
 * the two screens must agree — `100svh` in particular is the sort of value that
 * gets revisited for iOS toolbar behaviour, and it would otherwise get fixed on
 * one screen only.
 */
export const gateScreenLayout = css`
  width: 100%;
  /* Root pads by the rolling header's strip; without this every gate scrolls 2rem. */
  min-height: calc(100svh - var(--rolling-header-offset, 0px));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 1.5rem;
  box-sizing: border-box;

  @media ${({ theme }) => theme.breakpoints.sm} {
    padding: 0 1rem;
  }
`;
